<template>
  <div class="small">
    <Loader v-if="isLoading" bgColor="#ffffff"></Loader>
    <line-chart v-else :chart-data="datacollection" :options="options"></line-chart>
  </div>
</template>

<script>
import Loader from '../common/Loader'
import LineChart from './BarChart'
import moment from 'moment'
import docendo from '../../docendo'

export default {
  components: {
    LineChart,
    Loader
  },
  props: {
    calendarId: {
      default: 7
    },
    startDate: {
      default: null
    },
    endDate: {
      default: null
    }
  },
  data () {
    return {
      isLoading: true,
      debounceTimer: null,
      options: {
        indexAxis: 'x',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                color: 'rgba(0, 0, 0, 0)'
              },
              stacked: true,
              ticks: {
                beginAtZero: true
              }
            },
            {
              id: 'x2',
              display: false,
              stacked: false,
              offset: true,
              padding: 2,
              ticks: {
                beginAtZero: true
              },
              gridLines: {
                drawOnChartArea: false // only want the grid lines for one axis to show up
              }
            }
          ],
          yAxes: [
            {
              ticks: {
                suggestedMax: 40,
                beginAtZero: true
              }
            }
          ]
        }
      },
      dbHours: [],
      datacollection: null,
      dataPresence: [],
      dataTeaching: [],
      dataTasks: [],
      dataPreparation: [],
      dataAbsence: [],
      dataClub: [],
      dataWithKids: [],
      dataWithoutKids: [],
      dataPool: [],
      weeks: [],
      weekNumbersLabels: []
      // dataPresence: [34, 32, 35, 46, 39, 22, 37, 42, 0, 37, 37, 37, 37, 37, 37, 37, 34, 37, 37, 0, 37, 37, 37, 37, 37, 37, 37, 0, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37, 37],
      // dataTeaching: [9, 27, 29, 31, 18, 12, 18, 18, 0, 21, 21, 21, 21, 21, 21, 21, 21, 21, 12, 0, 21, 21, 21, 21, 21, 21, 21, 0, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21],
      // dataTasks: [2, 4, 1, 2, 3.7, 4, 6, 4, 0, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 0, 2, 2, 2, 2, 2, 2, 2, 0, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 5, 2, 3, 4, 3, 2, 2, 2, 2]
    }
  },
  computed: {
    masterTypes () {
      let types = []
      this.dbHours.forEach(hours => {
        let idx = types.indexOf(hours.master_type)
        if (idx < 0) {
          types.push(hours.master_type)
        }
      })
      return types
    }
  },
  watch: {
    startDate (val) {
      if (moment(val).isValid()) {
        this.callAPI()
      }
    },
    endDate (val) {
      if (moment(val).isValid()) {
        this.callAPI()
      }
    }
  },
  methods: {
    createWeeks () {
      this.dataPreparation = []
      this.dataTeaching = []
      this.dataTasks = []
      this.dataPresence = []
      this.dataAbsence = []
      this.weeks = []
      this.weekNumbersLabels = []
      this.dataClub = []
      this.dataWithKids = []
      this.dataWithoutKids = []
      this.dataPool = []
      this.dbHours.forEach(hours => {
        const idx = this.weeks.indexOf(hours.week)
        if (idx < 0) {
          this.weeks.push(hours.week)
          const mom = moment(hours.week * 1)
          const weekNumber = mom.isoWeek()
          this.weekNumbersLabels.push(weekNumber)
        }
      })

      function twoDigits (val) {
        return parseFloat(Number(val)).toFixed(2)
      }

      this.dbHours.forEach(hours => {
        let idxForWeek = this.weeks.indexOf(hours.week)
        switch (hours.master_type) {
          case 'preparation':
            this.dataPreparation[idxForWeek] = twoDigits(hours.coalesce)
            break
          case 'teaching':
            this.dataTeaching[idxForWeek] = twoDigits(hours.coalesce)
            break
          case 'other':
            this.dataTasks[idxForWeek] = twoDigits(hours.coalesce)
            break
          case 'presence':
            this.dataPresence[idxForWeek] = twoDigits(hours.coalesce)
            break
          case 'absence':
            this.dataAbsence[idxForWeek] = twoDigits(hours.coalesce)
            break
          case 'club':
            this.dataClub[idxForWeek] = twoDigits(hours.coalesce)
            break
          case 'with_kids':
            this.dataWithKids[idxForWeek] = twoDigits(hours.coalesce)
            break
          case 'without_kids':
            this.dataWithoutKids[idxForWeek] = twoDigits(hours.coalesce)
            break
          case 'pool':
            this.dataPool[idxForWeek] = twoDigits(hours.coalesce)
        }
      })
      this.fillData()
    },
    callAPI () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(_ => {
        this.getData()
      }, 250)
    },
    fillData () {
      const dataset = []
      const arrOfTypes = [
        {arr: this.dataPresence, value: 'presence'},
        {arr: this.dataTeaching, value: 'teaching'},
        {arr: this.dataTasks, value: 'tasks'},
        {arr: this.dataPreparation, value: 'preparation'},
        {arr: this.dataClub, value: 'club'},
        {arr: this.dataWithKids, value: 'with_kids'},
        {arr: this.dataWithoutKids, value: 'without_kids'},
        {arr: this.dataPool, value: 'pool'}
      ]
      const datasetLabels = [
        {
          data: this.dataPresence,
          type: 'bar',
          label: this.$t('vue2.task_overview.work_hours'),
          backgroundColor: 'rgba(153, 197, 244, 0.4)',
          barPercentage: 1,
          order: 5,
          xAxisID: 'x2',
          v_valueType: 'presence'
        },
        {
          data: this.dataTeaching,
          type: 'bar',
          label: this.$t('vue2.task_overview.teaching'),
          backgroundColor: 'rgba(122, 115, 255, .4)',
          barPercentage: 0.5,
          stack: '1',
          order: 1,
          v_valueType: 'teaching'
        },
        {
          data: this.dataTasks,
          label: this.$t('vue2.task_overview.tasks'),
          type: 'bar',
          backgroundColor: 'rgba(255, 217, 106, 1)',
          barPercentage: 0.5,
          stack: '1',
          order: 2,
          v_valueType: 'tasks'
        }, {
          data: this.dataPreparation,
          type: 'bar',
          label: this.$t('vue2.task_overview.preparation'),
          backgroundColor: '#eee',
          barPercentage: 0.5,
          stack: '1',
          order: 3,
          v_valueType: 'preparation'
        },
        {
          data: this.dataClub,
          type: 'bar',
          label: this.$t('vue2.task_overview.club'),
          backgroundColor: '#f3b09b',
          barPercentage: 0.5,
          stack: '1',
          order: 4,
          v_valueType: 'club'
        },
        {
          data: this.dataWithKids,
          type: 'bar',
          label: 'Med børn',
          backgroundColor: 'rgba(252, 127, 92,.6)',
          barPercentage: 0.5,
          stack: '1',
          order: 4,
          v_valueType: 'with_kids'
        },
        {
          data: this.dataWithoutKids,
          type: 'bar',
          label: 'Uden børn',
          backgroundColor: 'rgba(128, 128, 128,.6)',
          barPercentage: 0.5,
          stack: '1',
          order: 4,
          v_valueType: 'without_kids'
        },
        {
          data: this.dataPool,
          type: 'bar',
          label: this.$t('vue2.task_overview.pool'),
          backgroundColor: 'rgba(238, 174, 101,.6)',
          barPercentage: 0.5,
          stack: '1',
          order: 4,
          v_valueType: 'pool'
        },
        {
          data: this.dataAbsence,
          type: 'bar',
          label: this.$t('vue2.task_overview.absence'),
          backgroundColor: 'rgba(86,68,55,.6)',
          barPercentage: 0.5,
          stack: '1',
          order: 4,
          v_valueType: 'absence'
        }
      ]
      arrOfTypes.forEach(type => {
        if (type.arr.length > 0) {
          const idx = datasetLabels.findIndex(label => label.v_valueType === type.value)
          if (idx > -1) {
            dataset.push(datasetLabels[idx])
          }
        }
      })
      this.datacollection = {
        labels: this.weekNumbersLabels,
        datasets: dataset
      }
      this.isLoading = false
    },
    getData () {
      this.isLoading = true
      if (!this.startDate && !this.endDate || !moment(this.startDate).isValid() || !moment(this.endDate).isValid()) {
        return
      }
      let startDate = moment(this.startDate).format('YYYY-MM-DD')
      let endDate = moment(this.endDate).add(1, 'days').format('YYYY-MM-DD')
      docendo.calendarActualHoursPerWeek(this.calendarId, startDate, endDate, success => {
        this.dbHours = success
        this.createWeeks()
      }, failure => {
        console.log('failure')
      })
    }
  },
  mounted () {
    // this.createWeeks()
    this.callAPI()
  }
}
</script>

<style>
.small canvas {
  width: 100% !important;
  height: 350px !important;
}
@media print {
  .small .chartjs-size-monitor {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// UserTasksChart.vue?6f0541c4